
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EnrichmentJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  totalItems: number;
  processedItems: number;
  failedItems: number;
  progress: number;
  relevanceContext?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  errorMessage?: string;
  recentFailures: Array<{
    investor_original_id: string;
    error_message: string;
  }>;
}

export const useEnrichmentQueue = () => {
  const [currentJob, setCurrentJob] = useState<EnrichmentJob | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState<any>(null);

  // Store API keys
  const storeApiKeys = useCallback(async (openaiKey?: string, perplexityKey?: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('store-api-keys', {
        body: { openaiKey, perplexityKey }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success('API keys stored securely');
      return true;
    } catch (error) {
      console.error('Error storing API keys:', error);
      toast.error('Failed to store API keys');
      return false;
    }
  }, []);

  // Submit enrichment job
  const submitEnrichmentJob = useCallback(async (investorIds: string[], relevanceContext?: string) => {
    try {
      setIsSubmitting(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('submit-enrichment-job', {
        body: { investorIds, relevanceContext }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const { jobId } = response.data;
      toast.success(`Enrichment job submitted with ${investorIds.length} investors`);
      
      // Start monitoring the job
      await getJobStatus(jobId);
      
      return jobId;
    } catch (error) {
      console.error('Error submitting enrichment job:', error);
      toast.error('Failed to submit enrichment job');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // Cancel enrichment job
  const cancelEnrichmentJob = useCallback(async (jobId: string, reason?: string) => {
    try {
      setIsCancelling(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('cancel-enrichment-job', {
        body: { jobId, reason: reason || 'User cancelled' }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success('Job cancelled successfully');
      
      // Refresh job status
      await getJobStatus(jobId);
      
      return true;
    } catch (error) {
      console.error('Error cancelling enrichment job:', error);
      toast.error('Failed to cancel job');
      return false;
    } finally {
      setIsCancelling(false);
    }
  }, []);

  // Get job status
  const getJobStatus = useCallback(async (jobId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('get-job-status', {
        body: {},
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setCurrentJob(response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting job status:', error);
      return null;
    }
  }, []);

  // Set up real-time monitoring
  const setupRealtimeMonitoring = useCallback((jobId: string) => {
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
    }

    const channel = supabase
      .channel('enrichment-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'enrichment_jobs',
          filter: `id=eq.${jobId}`
        },
        (payload) => {
          console.log('Job update received:', payload);
          if (payload.new) {
            setCurrentJob(prev => prev ? {
              ...prev,
              status: payload.new.status,
              processedItems: payload.new.processed_items,
              failedItems: payload.new.failed_items,
              progress: payload.new.total_items > 0 ? (payload.new.processed_items / payload.new.total_items) * 100 : 0,
              updatedAt: payload.new.updated_at,
              completedAt: payload.new.completed_at,
              cancelledAt: payload.new.cancelled_at,
              cancellationReason: payload.new.cancellation_reason,
              errorMessage: payload.new.error_message
            } : null);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'enrichment_job_items'
        },
        (payload) => {
          console.log('Job item update received:', payload);
          // Refresh job status when items are updated
          if (currentJob) {
            getJobStatus(currentJob.jobId);
          }
        }
      )
      .subscribe();

    setRealtimeChannel(channel);
  }, [realtimeChannel, currentJob, getJobStatus]);

  // Clean up realtime subscription
  useEffect(() => {
    return () => {
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
      }
    };
  }, [realtimeChannel]);

  return {
    currentJob,
    isSubmitting,
    isCancelling,
    storeApiKeys,
    submitEnrichmentJob,
    cancelEnrichmentJob,
    getJobStatus,
    setupRealtimeMonitoring
  };
};
